import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { AlertTriangle, ArrowLeft, CheckCircle2, FileText, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import type { SourceSet } from '../types/sourceSet';

function asArray(value: any): any[] {
  return Array.isArray(value) ? value : [];
}

function renderItem(value: any): string {
  if (typeof value === 'string') return value;

  if (value && typeof value === 'object') {
    if (value.term && value.definition) return `${value.term}: ${value.definition}`;
    if (value.title && value.description) return `${value.title}: ${value.description}`;
    if (value.topic && value.reason) return `${value.topic}: ${value.reason}`;
    return JSON.stringify(value);
  }

  return String(value ?? '');
}

export function SourceSetSummary() {
  const { sourceSetId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [sourceSet, setSourceSet] = useState<SourceSet | null>(null);
  const [brief, setBrief] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadSummary = async () => {
      if (!sourceSetId || !user?.uid) return;

      setLoading(true);
      setError('');

      try {
        const sourceSetSnapshot = await getDoc(doc(db, 'sourceSets', sourceSetId));

        if (!sourceSetSnapshot.exists()) {
          setError('Source Set was not found.');
          setSourceSet(null);
          setBrief(null);
          return;
        }

        const sourceSetData = sourceSetSnapshot.data() as SourceSet;

        if (sourceSetData.ownerId !== user.uid) {
          setError('You do not have access to this Source Set summary.');
          setSourceSet(null);
          setBrief(null);
          return;
        }

        setSourceSet(sourceSetData);

        const briefId = searchParams.get('briefId') || sourceSetData.documentBriefId;

        if (!briefId) {
          setError('No generated summary is available for this Source Set yet.');
          setBrief(null);
          return;
        }

        const briefSnapshot = await getDoc(doc(db, 'documentBriefs', briefId));

        if (!briefSnapshot.exists()) {
          setError('The generated summary could not be found.');
          setBrief(null);
          return;
        }

        setBrief({ id: briefSnapshot.id, ...briefSnapshot.data() });
      } catch (err: any) {
        console.error('Failed to load Source Set summary:', err);
        setError(err?.message || 'Failed to load Source Set summary.');
      } finally {
        setLoading(false);
      }
    };

    loadSummary();
  }, [sourceSetId, searchParams, user?.uid]);

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-main text-text-main flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-9 h-9 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-text-alt">Loading Source Set summary...</p>
        </div>
      </div>
    );
  }

  if (error || !brief) {
    return (
      <div className="min-h-screen bg-bg-main text-text-main px-4 py-8">
        <div className="max-w-3xl mx-auto bg-bg-alt border border-border-main rounded-3xl p-8">
          <AlertTriangle className="w-10 h-10 text-rose-500 mb-4" />
          <h1 className="text-2xl font-bold mb-2">Summary unavailable</h1>
          <p className="text-text-alt mb-6">{error || 'The requested summary could not be loaded.'}</p>
          <Link to={`/source-sets/${sourceSetId}`} className="inline-flex items-center gap-2 text-primary font-semibold">
            <ArrowLeft className="w-4 h-4" />
            Back to Source Set
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-main text-text-main px-4 py-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <Link
            to={`/source-sets/${sourceSetId}`}
            className="inline-flex items-center gap-2 text-primary font-semibold mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Source Set
          </Link>

          <div className="bg-bg-alt border border-border-main rounded-3xl p-8">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                <FileText className="w-7 h-7 text-emerald-400" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-emerald-400 mb-2">
                  Generated Source Set Summary
                </p>
                <h1 className="text-3xl font-bold text-text-main">
                  {brief.documentTitle || sourceSet?.title || 'Document Summary'}
                </h1>
                <p className="text-text-alt mt-2">
                  Review the generated summary created from the uploaded Source Set materials.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-bg-alt border border-border-main rounded-3xl p-8 space-y-8">
          {brief.executiveBrief && (
            <section>
              <h2 className="text-xl font-bold text-text-main mb-3">Executive Brief</h2>
              <p className="text-text-alt leading-relaxed text-lg">{brief.executiveBrief}</p>
            </section>
          )}

          {brief.shortSummary && (
            <section>
              <h2 className="text-xl font-bold text-text-main mb-3">Short Summary</h2>
              <p className="text-text-alt leading-relaxed">{brief.shortSummary}</p>
            </section>
          )}

          {asArray(brief.keyPoints).length > 0 && (
            <section>
              <h2 className="text-xl font-bold text-text-main mb-3">Key Points</h2>
              <ul className="space-y-3">
                {asArray(brief.keyPoints).map((point, index) => (
                  <li key={`key-point-${index}`} className="flex gap-3 text-text-alt leading-relaxed">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                    <span>{renderItem(point)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {asArray(brief.actionItems).length > 0 && (
            <section>
              <h2 className="text-xl font-bold text-text-main mb-3">Action Items</h2>
              <ul className="list-disc pl-6 space-y-2 text-text-alt">
                {asArray(brief.actionItems).map((item, index) => (
                  <li key={`action-item-${index}`}>{renderItem(item)}</li>
                ))}
              </ul>
            </section>
          )}

          {asArray(brief.importantTerms).length > 0 && (
            <section>
              <h2 className="text-xl font-bold text-text-main mb-3">Important Terms</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {asArray(brief.importantTerms).map((term, index) => (
                  <div key={`important-term-${index}`} className="rounded-2xl border border-border-main bg-bg-main p-4">
                    <p className="text-text-alt text-sm leading-relaxed">{renderItem(term)}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {asArray(brief.suggestedLearningPath).length > 0 && (
            <section>
              <h2 className="text-xl font-bold text-text-main mb-3">Suggested Learning Path</h2>
              <ol className="list-decimal pl-6 space-y-2 text-text-alt">
                {asArray(brief.suggestedLearningPath).map((step, index) => (
                  <li key={`learning-path-${index}`}>{renderItem(step)}</li>
                ))}
              </ol>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
